import {
  Linkedin,
  Chrome,
  Bookmark,
  ListTodo,
  Download,
  MonitorSmartphone,
  ShieldCheck,
  Sparkles,
  UserPlus,
  MessagesSquare,
  BookOpen,
} from 'lucide-react';
import { useUiStore, type LinkedInTab } from '../../store/ui';
import { SavedItemsContent } from './SavedItemsView';
import { ExtTasksSection, ExtUpdateBanner } from '../settings/ExtensionTab';
import { useExtUpdateAvailable } from '../../hooks/useExtTokens';
import { EXT_VERSION, EXT_ZIP_URL } from '../../lib/extension';
import { HelpHint } from '../ui/HelpHint';


// Tab interni della pagina LinkedIn (selezionabili anche dalle sottovoci sidebar)
const TABS: { key: LinkedInTab; label: string; icon: typeof Chrome }[] = [
  { key: 'extension', label: 'Estensione', icon: Chrome },
  { key: 'saved', label: 'Feed', icon: Bookmark },
  { key: 'queue', label: 'Coda attività', icon: ListTodo },
  { key: 'manual', label: 'Manuale', icon: BookOpen },
];

/**
 * Pagina LinkedIn: raccoglie tutto ciò che riguarda l'Assistente LinkedIn.
 * Tab interni: Estensione (presentazione + download + guida), Salvati (riusa
 * SavedItemsContent) e Coda attività (riusa ExtTasksSection delle Impostazioni).
 * Il tab attivo vive nello store UI (linkedinTab) così le sottovoci della
 * sidebar possono selezionarlo direttamente.
 */
export function LinkedInView() {
  const tab = useUiStore((s) => s.linkedinTab);
  const setTab = useUiStore((s) => s.setLinkedinTab);
  const extUpdate = useExtUpdateAvailable();
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const setManualiTarget = useUiStore((s) => s.setManualiTarget);
  const setManualiScope = useUiStore((s) => s.setManualiScope);

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Sotto-sidebar verticale: stessa struttura a menu della sezione Posta.
          Le voci (Estensione/Salvati/Coda) sono in colonna a sinistra. */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-zinc-800/80 bg-zinc-950/30">
        <div className="flex items-center gap-2 border-b border-zinc-800/80 px-3 pb-3 pt-4">
          <Linkedin size={16} className="text-accent-400" />
          <h1 className="text-[15px] font-semibold text-accent-200">LinkedIn</h1>
          <HelpHint topic="linkedin" />
        </div>
        <nav className="flex flex-col gap-0.5 p-2">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                onClick={() => {
                  if (key === 'manual') {
                    // Apre il manuale già scrollato alle FAQ dell'Assistente LinkedIn.
                    setManualiTarget('linkedin');
                    setManualiScope('linkedin');
                    setCurrentView('manuali');
                  } else {
                    setTab(key);
                  }
                }}
                className={
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium transition ' +
                  (active
                    ? 'bg-accent-500/15 text-accent-200'
                    : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-zinc-100')
                }
              >
                <Icon size={14} className={active ? 'text-accent-400' : 'text-zinc-500'} /> {label}
                {key === 'extension' && extUpdate && (
                  <span
                    className="ml-auto h-2 w-2 animate-pulse rounded-full bg-violet-400"
                    title="Nuova versione dell'estensione disponibile"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Contenuto della voce selezionata */}
      <section className="wm-list flex flex-1 flex-col overflow-hidden">
        {/* Banner "nuova versione estensione": evidente in cima alla sezione,
            visibile da ogni tab (non solo dentro Estensione). */}
        {extUpdate && (
          <div className="shrink-0 px-4 pt-3">
            <ExtUpdateBanner />
          </div>
        )}
        {tab === 'saved' ? (
          <SavedItemsContent />
        ) : tab === 'queue' ? (
          <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3">
            {/* Coda copilota: stessa sezione (e grafica elenchi webmail) delle Impostazioni → Estensione */}
            <ExtTasksSection />
          </div>
        ) : (
          <ExtensionIntro />
        )}
      </section>
    </div>
  );
}

// Tab "Estensione": hero + sezione help completa in tre blocchi ben leggibili:
// "Cosa puoi fare", "Installazione e configurazione" e "Domande frequenti".
function ExtensionIntro() {
  const openSettings = useUiStore((s) => s.openSettings);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const setManualiTarget = useUiStore((s) => s.setManualiTarget);
  const setManualiScope = useUiStore((s) => s.setManualiScope);

  // Cosa fa l'estensione, una riga per funzione (icona + titolo + descrizione)
  const FEATURES: { icon: typeof Bookmark; title: string; desc: string }[] = [
    {
      icon: Bookmark,
      title: 'Salva post',
      desc: 'Sui post del feed compare il bottone 💾: un click e il post arriva nella webmail.',
    },
    {
      icon: UserPlus,
      title: 'Salva contatto',
      desc: 'Dalla pagina di un profilo salvi la persona nel CRM con un profilo ricco: nome, ruolo, azienda, formazione, località, follower e collegamenti, badge verificato — ed email, telefono e siti se apri tu il pannello «Informazioni di contatto». Tutto compare nella scheda del contatto.',
    },
    {
      icon: MessagesSquare,
      title: 'Salva engagement',
      desc: 'Salva un post insieme a chi lo ha commentato (espandi prima i commenti): ogni commentatore diventa un contatto.',
    },
    {
      icon: ListTodo,
      title: 'Coda copilota',
      desc: 'Pianifichi le attività dalla webmail (es. «visita questo profilo»); l’estensione te le ricorda e le esegui tu su LinkedIn.',
    },
    {
      icon: Sparkles,
      title: 'Riassunti e tag AI automatici',
      desc: 'Ogni elemento salvato viene riassunto e taggato dall’AI, pronto da ritrovare nel tab Feed.',
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6 rounded-2xl border border-zinc-800/80 bg-zinc-950 p-6">
        {/* Hero con le due azioni principali */}
        <div className="wm-li-hero p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-500/15">
              <Linkedin size={22} className="text-accent-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-100">Assistente LinkedIn</h2>
              <p className="text-xs text-zinc-500">Estensione Chrome collegata alla tua webmail</p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300">
            L&rsquo;Assistente LinkedIn salva <b>post, contatti ed engagement</b> da LinkedIn
            direttamente nella webmail: gli elementi arrivano nel tab <b>Feed</b>, già
            riassunti e taggati dall&rsquo;AI, e i contatti finiscono nel CRM.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href={EXT_ZIP_URL}
              download
              className="wm-btn-accent flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition"
            >
              <Download size={15} /> Scarica l&rsquo;estensione (v{EXT_VERSION})
            </a>
            <button
              onClick={() => openSettings('extension')}
              className="wm-btn-ghost flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition"
            >
              <MonitorSmartphone size={14} /> Dispositivi collegati
            </button>
            <button
              onClick={() => {
                // Apre il manuale già scrollato alle FAQ dell'Assistente LinkedIn.
                setManualiTarget('linkedin');
                setManualiScope('linkedin');
                setCurrentView('manuali');
              }}
              className="wm-btn-ghost flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition"
            >
              <BookOpen size={14} /> Apri il manuale
            </button>
          </div>
        </div>

        {/* Cosa puoi fare: una riga per funzione, con icona */}
        <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/30 p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-zinc-100">
            <Chrome size={13} className="text-accent-400" /> Cosa puoi fare
          </h3>
          <ul className="mt-3 space-y-3">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <li key={title} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-500/15">
                  <Icon size={14} className="text-accent-400" />
                </span>
                <div>
                  <div className="text-xs font-semibold text-zinc-200">{title}</div>
                  <p className="text-xs leading-relaxed text-zinc-400">{desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Nota anti-ban in evidenza */}
        <div className="flex items-start gap-2.5 rounded-lg border border-emerald-800/40 bg-emerald-950/20 p-4">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-400" />
          <p className="text-xs leading-relaxed text-zinc-400">
            <b className="text-emerald-300">Sicura per il tuo account:</b> l&rsquo;estensione non
            automatizza nulla. Ogni azione su LinkedIn la fai tu, a mano: l&rsquo;assistente si
            limita a salvare ciò che scegli e a ricordarti le attività in coda (anti-ban).
          </p>
        </div>
      </div>
    </div>
  );
}
